'use client'
import Link from '@/components/router/Link'
import { IAppConfig } from '@/data/config/types'
import { Search, X } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { ChevronLeft } from 'react-feather'
import {
  imagePath,
  PartnerAtlasPath,
  rootPath,
  searchPath
} from 'shared/urlHelpers'
import NewHeaderMenuButton from './NewHeaderMenuButton'
import NewHeaderUserInfo from './NewHeaderUserInfo'
import NewShortcuts from './NewShortcuts'
import { isInAppBrowser } from 'lib/domHelpers'

export default function Header({
  appConfig
}: {
  appConfig: IAppConfig
}): React.ReactNode {
  const [searchOpen, setSearchOpen] = useState(false)
  const [, setIsAnimating] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const [searchValue, setSearchValue] = useState('')

  const isInApp = isInAppBrowser()

  const { push, back } = useRouter()

  // Scroll to top on back navigation
  useEffect(() => {
    const handlePopState = () => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 0)
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const toggleSearch = () => {
    setIsAnimating(true)

    if (!searchOpen) {
      setSearchOpen(true)
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    } else {
      setTimeout(() => {
        setSearchOpen(false)
        setIsAnimating(false)
        setSearchValue('')
      }, 150)
    }
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (searchValue) {
      push(searchPath({ q: searchValue }))
      toggleSearch()
    }
  }
  return (
    <div className="relative">
      <header className="pt-4 lg:pt-5 bg-white/[.975] backdrop-blur-sm z-[9999999]">
        <div className="container lg:!w-full lg:!px-0 !max-w-[990px] xl:!max-w-[1200px] !mb-4 block relative z-10">
          <div className="flex justify-between items-center gap-6 w-full">
            <div className="flex items-center shrink-0 gap-2">
              <NewHeaderMenuButton />
              <button
                onClick={() => back()}
                className="group shrink-0 w-10 h-10 flex items-center justify-center"
              >
                <ChevronLeft className="rounded-full relative p-1 h-9 w-9 text-gray-600 transition-transform duration-300 transform -translate-x-3 group-active:scale-75" />
              </button>
            </div>
            <div className="flex items-center justify-center flex-1">
              <Link href={rootPath()} className="logo-link w-10 lg:w-12 h-auto">
                <Image
                  src={imagePath('handball-net-logo-sm.svg')}
                  width={100}
                  height={100}
                  alt="Handball.net Logo"
                  className="w-full h-full"
                />
              </Link>
              <a
                href={PartnerAtlasPath()}
                target={isInApp ? '_self' : '_blank'}
                rel="noreferrer"
                className="block ml-2"
              >
                <img
                  src="/img/partners/atlas/Atlas_vertikal.webp"
                  className="max-h-5 lg:max-h-7 w-auto"
                  alt="Atlas Logo"
                />
              </a>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  onClick={toggleSearch}
                  className="focus:outline-none w-10 h-10 flex items-center justify-center"
                  aria-label={searchOpen ? 'Suche schließen' : 'Suche öffnen'}
                >
                  <div className="relative w-6 h-6 overflow-hidden">
                    <Search
                      className={`absolute inset-0 h-6 w-6 text-gray-600
                        transition-all ${
                          searchOpen
                            ? 'duration-300 ease-out'
                            : 'duration-200 ease-in'
                        } transform-gpu
                        ${
                          searchOpen
                            ? 'opacity-0 rotate-225 scale-0 translate-y-3'
                            : 'opacity-100 rotate-0 scale-100 translate-y-0'
                        }`}
                      strokeWidth={2.5}
                    />

                    <X
                      className={`absolute inset-0 h-6 w-6 text-gray-600
                        transition-all ${
                          searchOpen
                            ? 'duration-300 ease-out'
                            : 'duration-150 ease-in'
                        } transform-gpu
                        ${
                          searchOpen
                            ? 'opacity-100 rotate-0 scale-100 translate-y-0'
                            : 'opacity-0 -rotate-225 scale-0 -translate-y-3'
                        }`}
                      strokeWidth={2.5}
                    />
                  </div>
                </button>
              </div>
              <div className="hidden lg:flex shrink-0">
                <NewHeaderUserInfo />
              </div>
            </div>
          </div>
        </div>
        <NewShortcuts appConfig={appConfig} />
      </header>

      {/* Overlay mit gestaffelter Animation - Background zuerst */}
      <div
        className={`fixed inset-0 bg-white/90 backdrop-blur-md flex flex-col pt-4 z-[9999999]
          transition-all ${
            searchOpen ? 'duration-250 ease-out' : 'duration-150 ease-in'
          }
          ${searchOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      >
        <div className="container mx-auto px-3 flex items-center">
          <div
            ref={searchContainerRef}
            className={`relative flex-1 flex items-center max-w-lg
    transition-all ${
      searchOpen
        ? 'duration-300 delay-75 ease-out'
        : 'duration-150 delay-0 ease-in'
    }
    ${searchOpen ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'}
 -translate-y-2
  `}
          >
            <div className="flex items-center bg-gray-100 px-4 py-3 w-full rounded-lg shadow-sm border border-gray-400 hover:border-gray-900 transition-all duration-200">
              <Search className="h-5 w-5 text-gray-500 mr-2" />
              <form onSubmit={onSubmit}>
                <input
                  ref={searchInputRef}
                  placeholder="Suchen..."
                  enterKeyHint="search"
                  className="flex-1 bg-transparent border-none text-gray-800 text-lg focus:outline-none focus:ring-0"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value)
                  }}
                />
              </form>
            </div>
          </div>

          <div className="w-10 h-10 flex items-center justify-center -translate-y-3">
            <button
              onClick={toggleSearch}
              className={`
      focus:outline-none w-10 h-10 flex items-center justify-center
      transition-all
      ${
        searchOpen
          ? 'duration-300 delay-100 ease-out'
          : 'duration-100 delay-0 ease-in'
      }
      ${
        searchOpen
          ? 'opacity-100 translate-x-0 scale-100'
          : 'opacity-0 translate-x-5 scale-75'
      }
    `}
              aria-label="Suche schließen"
            >
              <div className="relative w-6 h-6 overflow-hidden">
                <X
                  className="
                  absolute inset-0 h-6 w-6 text-gray-700 hover:text-gray-900
                  transition-transform duration-150 hover:scale-110
                  rotate-0 scale-100 opacity-100 -translate-y-0"
                  strokeWidth={2.5}
                />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
